import React from 'react';

import kebabCase from 'lodash/kebabCase';

import { useClassnames } from '@lumapps/classnames';
import { ContentPreviewButton } from '@lumapps/content-preview/components/ContentPreviewButton';
import { newTemplateFromContentRoute } from '@lumapps/content-templates/routes';
import { contentTypeAdminContent } from '@lumapps/content-types-admin-content/routes';
import { isContent } from '@lumapps/content-types/utils';
import { AddContentToMainNavigationDialog } from '@lumapps/contents/components/AddContentToMainNavigationDialog';
import { CONTRIBUTION_MODES } from '@lumapps/contents/constants';
import {
    canUserDesignCurrentContent,
    canDeleteContent,
    getCurrentContentId,
    getCurrentContentPublicationStatus,
    getCurrentContentStatus,
    getCurrentContentType,
    getCurrentContributionMode,
    getCurrentCustomContentTypeId,
    isCurrentContentPublishedHomepage,
} from '@lumapps/contents/ducks/selectors';
import { useContentDelete } from '@lumapps/contents/hooks/useContentDelete';
import { CONTENTS } from '@lumapps/contents/keys';
import { contentCopy } from '@lumapps/contents/routes';
import { LegacyContentStatus } from '@lumapps/contents/types';
import { useDataAttributes } from '@lumapps/data-attributes';
import { WidgetsTranslationDialog } from '@lumapps/designer-translations/components/WidgetsTranslationDialog';
import { DESIGNER_TRANSLATIONS } from '@lumapps/designer-translations/keys';
import { CONTRIBUTION_MODE_MAP, SELECTABLE_CONTRIBUTION_MODES } from '@lumapps/designer/constants';
import { DESIGNER } from '@lumapps/designer/keys';
import { isFeatureEnabled } from '@lumapps/features';
import { ConfirmDialog } from '@lumapps/lumx-dialogs/components/ConfirmDialog';
import { usePromiseConfirmDialog } from '@lumapps/lumx-dialogs/hooks/usePromiseConfirmDialog';
import { Menubar } from '@lumapps/lumx-menu/components/Menubar';
import {
    mdiContentDuplicate,
    mdiHistory,
    mdiMenu,
    mdiTranslate,
    mdiTrashCanOutline,
    mdiViewCompactOutline,
} from '@lumapps/lumx/icons';
import { GenericBlock, Icon, Text } from '@lumapps/lumx/react';
import { hasMenuEditRights } from '@lumapps/navigation-admin/ducks/selectors';
import { isInstanceSuperAdmin } from '@lumapps/permissions';
import { useSelector, useDispatch } from '@lumapps/redux/react';
import { useRouter } from '@lumapps/router';
import { actions as routerActions } from '@lumapps/router/ducks/slice';
import { getKeyForLanguage, GLOBAL, useTranslate } from '@lumapps/translations';
import { contributionLanguagesSelector, inputLanguageSelector } from '@lumapps/translations/ducks/selectors';

import { ContentEditorContext } from '../../context/ContentEditorContext';

import './index.scss';

export interface ContentEditorContextualActionsProps {
    /**
     * Enable the versioning mode.
     */
    enableVersioningMode: () => void;
    /** Check if content has changes in the parent legacy editor */
    hasChanges?: () => boolean;
    /** Whether the versionning panel is open or not. Information from legacy. */
    isVersionningPanelOpen?: boolean;
    /** Whether to show the version history button. Defaults to true. */
    showVersionHistory?: boolean;
}

const SCOPE = 'content-editor-contextual-actions';
const CLASSNAME = 'content-editor-contextual-actions';

/**
 *
 * @param ContentEditorContextualActionsProps
 * @returns ContentEditorContextualActions
 */
export const ContentEditorContextualActions: React.FC<ContentEditorContextualActionsProps> = ({
    enableVersioningMode,
    hasChanges,
    isVersionningPanelOpen,
    showVersionHistory = true,
}) => {
    const { get: getDataAttributes } = useDataAttributes(SCOPE);
    const { block } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();
    const dispatch = useDispatch();

    const [isWidgetsTranslationDialogOpen, setIsWidgetsTranslationDialogOpen] = React.useState(false);
    const [targetTranslationLanguage, setTargetTranslationLanguage] = React.useState<string>('en');
    const [isAddToNavDialogOpen, setIsAddToNavDialogOpen] = React.useState(false);

    const { publicationStatus, isAwaitingValidation } = useSelector(getCurrentContentPublicationStatus);
    const legacyStatus = useSelector(getCurrentContentStatus);
    const contentType = useSelector(getCurrentContentType);
    const customContentType = useSelector(getCurrentCustomContentTypeId);
    const contentId = useSelector(getCurrentContentId);
    const isHomepage = useSelector(isCurrentContentPublishedHomepage);

    /** Whether the content has been saved once. */
    const isSavedOnce = contentId !== undefined;

    const hasDeleteRights = useSelector(canDeleteContent(customContentType));
    const canDelete = hasDeleteRights && !isHomepage && isSavedOnce;

    const isPublished = publicationStatus === 'published';
    const isScheduled = publicationStatus === 'scheduled';
    const isArchived = publicationStatus === 'archived';
    const isDraft = publicationStatus === 'draft';

    const isAdmin = useSelector(isInstanceSuperAdmin);
    const {
        state: contentEditorState,
        contentEditorActions: { updateContributionMode, onInputLanguageChange, updateContent, saveContent },
    } = React.useContext(ContentEditorContext) || {};

    // Contribution mode
    const currentContributionMode =
        useSelector(getCurrentContributionMode) || contentEditorState?.contributionMode || CONTRIBUTION_MODES.READER;
    const currentContributionModeProps = CONTRIBUTION_MODE_MAP[currentContributionMode] || {};

    const isReadModeOn = currentContributionMode === CONTRIBUTION_MODES.READER;
    const hasRightToDesign = useSelector(canUserDesignCurrentContent(customContentType!));
    const canChangeContributionMode = !!updateContributionMode && hasRightToDesign && !isArchived && !isReadModeOn;

    // Translate
    const inputLanguage = useSelector(inputLanguageSelector);
    const contributionLanguages = useSelector(contributionLanguagesSelector);
    const isEndUserTranslationEnabled = useSelector(isFeatureEnabled('translation-end-user'));
    // Remove the current input language
    const filteredContributionLanguages = contributionLanguages.filter((lang) => lang !== inputLanguage);
    const canTranslate = isSavedOnce && contributionLanguages.length > 1 && (isPublished || isScheduled || isDraft);

    const { redirect } = useRouter();

    // Set as template
    const canSetAsTemplate = isAdmin && isContent(contentType) && isSavedOnce;

    const canSeeHistory = showVersionHistory && !isArchived && isSavedOnce && !isAwaitingValidation;

    // Nav
    const hasNavigationRights = useSelector(hasMenuEditRights);
    const canAddToNavigation = hasNavigationRights && hasRightToDesign && (isPublished || isScheduled);

    // Preview is not available for archived contents
    const canPreview = !isArchived;

    const { props: confirmDialogProps, open: openConfirmDialog, close: closeConfirmDialog } = usePromiseConfirmDialog();

    const { onDelete } = useContentDelete({
        openConfirmDialog,
        contentId,
        onSuccess: () => {
            dispatch(routerActions.unblockRedirection());
            if (customContentType) {
                redirect(contentTypeAdminContent(customContentType));
            }
        },
    });

    const onDuplicate = React.useCallback(async () => {
        if (!isSavedOnce) {
            return;
        }
        const copyRoute = contentCopy(contentId, legacyStatus || LegacyContentStatus.draft);

        const shouldConfirm = hasChanges?.();
        if (shouldConfirm) {
            const confirmed = await openConfirmDialog({
                id: 'duplicate',
                title: translateKey(GLOBAL.UNSAVED_MODIFICATIONS),
                confirmMessage: translateKey(GLOBAL.UNSAVED_MODIFICATIONS_DESCRIPTION),
                cancelProps: { label: translateKey(GLOBAL.CANCEL) },
                confirmProps: { label: translateKey(GLOBAL.CONTINUE) },
            });

            if (confirmed) {
                redirect(copyRoute, { openInNewTab: true });
            } else {
                closeConfirmDialog();
            }
        } else {
            redirect(copyRoute, { openInNewTab: true });
        }
    }, [
        closeConfirmDialog,
        contentId,
        hasChanges,
        isSavedOnce,
        legacyStatus,
        openConfirmDialog,
        redirect,
        translateKey,
    ]);

    const onSetAsTemplate = React.useCallback(async () => {
        if (!isSavedOnce || !canSetAsTemplate) {
            return;
        }
        const setAsTemplateRoute = newTemplateFromContentRoute(contentId, customContentType!);

        const shouldConfirm = hasChanges?.();
        if (shouldConfirm) {
            const confirmed = await openConfirmDialog({
                id: 'duplicate',
                title: translateKey(GLOBAL.UNSAVED_MODIFICATIONS),
                confirmMessage: translateKey(GLOBAL.UNSAVED_MODIFICATIONS_DESCRIPTION),
                cancelProps: { label: translateKey(GLOBAL.CANCEL) },
                confirmProps: { label: translateKey(GLOBAL.CONTINUE) },
            });

            if (confirmed) {
                redirect(setAsTemplateRoute, { openInNewTab: true });
            } else {
                closeConfirmDialog();
            }
        } else {
            redirect(setAsTemplateRoute, { openInNewTab: true });
        }
    }, [
        isSavedOnce,
        canSetAsTemplate,
        contentId,
        customContentType,
        hasChanges,
        openConfirmDialog,
        translateKey,
        redirect,
        closeConfirmDialog,
    ]);

    const onTranslate = (targetLang: string) => {
        setTargetTranslationLanguage(targetLang);
        setIsWidgetsTranslationDialogOpen(true);
    };

    const addToNavigation = React.useCallback(() => {
        setIsAddToNavDialogOpen(true);
    }, []);

    const closeAddToNavDialog = React.useCallback(() => {
        setIsAddToNavDialogOpen(false);
    }, []);

    // View history mode is matching this.
    if (isVersionningPanelOpen) {
        return null;
    }

    return (
        <div className={block()}>
            {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
            {canTranslate && isWidgetsTranslationDialogOpen && (
                <WidgetsTranslationDialog
                    isOpen={isWidgetsTranslationDialogOpen}
                    onClose={() => setIsWidgetsTranslationDialogOpen(false)}
                    initTargetLanguages={[targetTranslationLanguage]}
                    scope="content-editor"
                    setInputLang={onInputLanguageChange}
                    saveContentAsDraft={saveContent}
                    updateLegacyContent={updateContent}
                />
            )}
            <AddContentToMainNavigationDialog
                isOpen={isAddToNavDialogOpen}
                onClose={closeAddToNavDialog}
                dialogProps={{ preventAutoClose: true }}
            />
            <Menubar ariaLabel="contextual actions" orientation="vertical">
                <Menubar.Section>
                    {canPreview && <ContentPreviewButton saveContentDraft={saveContent} />}
                    {canTranslate && (
                        <Menubar.ItemSubmenu
                            tooltipProps={{
                                placement: 'left',
                            }}
                            icon={mdiTranslate}
                            triggerProps={{
                                popoverProps: {
                                    offset: { away: 6, along: 6 },
                                },
                            }}
                            label={translateKey(CONTENTS.CONTEXTUAL_ACTIONS_TRANSLATION_OFF_ACTION)}
                            {...getDataAttributes({ element: 'translate' })}
                        >
                            {isEndUserTranslationEnabled && (
                                <Menubar.Section>
                                    <Menubar.Item
                                        key="all"
                                        onClick={() => onTranslate('all')}
                                        {...getDataAttributes({ element: 'translate-to-all-languages' })}
                                    >
                                        {translateKey(DESIGNER_TRANSLATIONS.ALL_LANGUAGES)}
                                    </Menubar.Item>
                                </Menubar.Section>
                            )}
                            {filteredContributionLanguages.map((lang) => (
                                <Menubar.Item
                                    key={lang}
                                    onClick={() => onTranslate(lang)}
                                    {...getDataAttributes({ element: `translate-to-${lang}` })}
                                >
                                    {translateKey(getKeyForLanguage(lang))}
                                </Menubar.Item>
                            ))}
                        </Menubar.ItemSubmenu>
                    )}
                </Menubar.Section>

                {canChangeContributionMode && (
                    <Menubar.Section>
                        <Menubar.ItemSubmenu
                            icon={currentContributionModeProps.icon}
                            tooltipProps={{
                                placement: 'left',
                            }}
                            triggerProps={{
                                popoverProps: {
                                    offset: { away: 6, along: 6 },
                                },
                            }}
                            {...getDataAttributes({
                                element: 'contribution-mode-select',
                            })}
                            label={translateKey(currentContributionModeProps.key)}
                        >
                            <Menubar.SectionRadioGroup
                                value={currentContributionMode}
                                onChange={(newMode) => updateContributionMode(newMode as CONTRIBUTION_MODES, true)}
                            >
                                {(
                                    Object.keys(
                                        SELECTABLE_CONTRIBUTION_MODES,
                                    ) as (keyof typeof SELECTABLE_CONTRIBUTION_MODES)[]
                                ).map((contribModeKey) => {
                                    const { icon, description, key } =
                                        SELECTABLE_CONTRIBUTION_MODES[contribModeKey] ?? {};

                                    return (
                                        <Menubar.ItemRadio
                                            aria-label={translateKey(key)}
                                            key={key}
                                            value={contribModeKey}
                                            truncate={false}
                                            {...getDataAttributes({
                                                element: 'contribution-mode-select',
                                                action: `switch-to-${kebabCase(contribModeKey)}`,
                                            })}
                                        >
                                            <GenericBlock as="span">
                                                {icon && (
                                                    <GenericBlock.Figure as="span">
                                                        <Icon icon={icon} size="xs" />
                                                    </GenericBlock.Figure>
                                                )}
                                                <GenericBlock.Content as="span">
                                                    <Text as="span" typography="body1" id="copy-link-label">
                                                        {translateKey(key)}
                                                    </Text>
                                                    {description && (
                                                        <Text
                                                            as="span"
                                                            typography="caption"
                                                            color="dark"
                                                            colorVariant="L2"
                                                        >
                                                            {translateKey(description)}
                                                        </Text>
                                                    )}
                                                </GenericBlock.Content>
                                            </GenericBlock>
                                        </Menubar.ItemRadio>
                                    );
                                })}
                            </Menubar.SectionRadioGroup>
                        </Menubar.ItemSubmenu>
                    </Menubar.Section>
                )}
                <Menubar.Section>
                    {canSeeHistory && (
                        <Menubar.Item
                            tooltipProps={{ placement: 'left' }}
                            icon={mdiHistory}
                            onClick={enableVersioningMode}
                            {...getDataAttributes({ element: 'show-version-history' })}
                        >
                            {translateKey(DESIGNER.SHOW_VERSION_HISTORY_LABEL)}
                        </Menubar.Item>
                    )}
                    {canAddToNavigation && (
                        <Menubar.Item
                            tooltipProps={{ placement: 'left' }}
                            icon={mdiMenu}
                            onClick={addToNavigation}
                            tooltipLabel={translateKey(DESIGNER.ADD_TO_NAVIGATION_TOOLTIP)}
                            {...getDataAttributes({ element: 'add-to-navigation' })}
                        >
                            {translateKey(DESIGNER.ADD_TO_NAVIGATION)}
                        </Menubar.Item>
                    )}
                    <Menubar.Item
                        tooltipProps={{ placement: 'left' }}
                        icon={mdiContentDuplicate}
                        isDisabled={!isSavedOnce}
                        onClick={onDuplicate}
                        {...getDataAttributes({ element: 'duplicate-content' })}
                    >
                        {translateKey(GLOBAL.DUPLICATE)}
                    </Menubar.Item>
                    {canSetAsTemplate && (
                        <Menubar.Item
                            tooltipProps={{ placement: 'left' }}
                            icon={mdiViewCompactOutline}
                            isDisabled={!isSavedOnce}
                            onClick={onSetAsTemplate}
                            {...getDataAttributes({ element: 'set-as-template' })}
                        >
                            {translateKey(DESIGNER.SET_AS_TEMPLATE)}
                        </Menubar.Item>
                    )}
                </Menubar.Section>
                <Menubar.Section>
                    {canDelete && (
                        <Menubar.Item
                            tooltipProps={{ placement: 'left' }}
                            icon={mdiTrashCanOutline}
                            onClick={onDelete}
                            {...getDataAttributes({ element: 'delete' })}
                        >
                            {translateKey(GLOBAL.DELETE)}
                        </Menubar.Item>
                    )}
                </Menubar.Section>
            </Menubar>
        </div>
    );
};
